import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  channelLabel,
  type ProductServiceKind,
  type ProductServiceStatus,
} from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useCreateProductService,
  useProductCategories,
  useProductService,
  useUpdateProductService,
} from '../../api/products-services';
import { useChannels } from '../../api/channels';
import { ProductImageField } from './ProductImageField';

const KIND_OPTIONS: { value: ProductServiceKind; label: string }[] = [
  { value: 'PRODUCT', label: 'Product (catalog item)' },
  { value: 'SERVICE', label: 'Service (can be bookable)' },
];

const STATUS_OPTIONS: { value: ProductServiceStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No unit' },
  { value: 'KG', label: 'Kilogram (KG)' },
  { value: 'GRAM', label: 'Gram' },
  { value: 'LITRE', label: 'Litre' },
  { value: 'PIECE', label: 'Piece' },
  { value: 'METER', label: 'Meter' },
  { value: 'FOOT', label: 'Foot' },
  { value: 'BOX', label: 'Box' },
];

function parseAliases(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const EXAMPLE_BOOKING = `{
  "slotMinutes": 30,
  "timezone": "UTC",
  "weeklyHours": {
    "mon": [["09:00", "17:00"]],
    "tue": [["09:00", "17:00"]],
    "wed": [["09:00", "17:00"]],
    "thu": [["09:00", "17:00"]],
    "fri": [["09:00", "17:00"]]
  }
}`;

/**
 * A SERVICE's booking schedule is authored as raw JSON here (same convention
 * as the automation template library's JSON definition textarea) rather than
 * a day-by-day picker UI - the server (`bookingConfig` in
 * apps/api products-services.schemas.ts) fully validates the shape either
 * way, and a weekly-hours grid is a low-frequency, one-time setup task.
 */
export function ProductServiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const bookingFieldId = useId();
  const categoryListId = useId();
  const categories = useProductCategories();
  const whatsappChannels = useChannels({ type: 'WHATSAPP' });
  const existing = useProductService(id);
  const create = useCreateProductService();
  const update = useUpdateProductService(id ?? '');

  const [kind, setKind] = useState<ProductServiceKind>('SERVICE');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceAmount, setPriceAmount] = useState('');
  const [unit, setUnit] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [localAliasesText, setLocalAliasesText] = useState('');
  const [status, setStatus] = useState<ProductServiceStatus>('ACTIVE');
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [bookingText, setBookingText] = useState(EXAMPLE_BOOKING);
  const [imageAssetId, setImageAssetId] = useState<string | null>(null);
  const [inWhatsAppCatalog, setInWhatsAppCatalog] = useState(false);
  const [category, setCategory] = useState('');
  /** [] = every number's catalogue. */
  const [catalogChannelIds, setCatalogChannelIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && existing.data) {
      const p = existing.data;
      setKind(p.kind);
      setName(p.name);
      setDescription(p.description ?? '');
      setPriceAmount(p.priceAmount !== null ? String(p.priceAmount) : '');
      setUnit(p.unit ?? '');
      setDiscountPercent(p.discountPercent !== null ? String(p.discountPercent) : '');
      setLocalAliasesText(p.localAliases.join(', '));
      setStatus(p.status);
      setBookingEnabled(Boolean(p.booking));
      if (p.booking) setBookingText(JSON.stringify(p.booking, null, 2));
      setImageAssetId(p.imageAssetId);
      setInWhatsAppCatalog(p.inWhatsAppCatalog);
      setCategory(p.category ?? '');
      setCatalogChannelIds(p.catalogChannelIds);
    }
  }, [isEdit, existing.data]);

  if (isEdit && existing.isPending) return <PageState kind="loading" message="Loading…" />;
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  const busy = create.isPending || update.isPending;

  function clearFieldError(k: string) {
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  }

  function validate(): { errors: Record<string, string>; booking?: unknown } {
    const errors: Record<string, string> = {};
    if (name.trim().length < 1) errors.name = 'Enter a name.';
    else if (name.trim().length > 200) errors.name = 'Name must be 200 characters or fewer.';

    if (priceAmount.trim() !== '' && Number.isNaN(Number(priceAmount))) {
      errors.priceAmount = 'Enter a number.';
    }

    if (category.trim().length > 60) errors.category = 'Category must be 60 characters or fewer.';

    if (discountPercent.trim() !== '') {
      const n = Number(discountPercent);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        errors.discountPercent = 'Enter a number between 0 and 100.';
      }
    }

    if (kind !== 'SERVICE' || !bookingEnabled) return { errors };

    let booking: unknown;
    try {
      booking = JSON.parse(bookingText);
    } catch {
      errors.booking = 'This is not valid JSON.';
      return { errors };
    }
    return { errors, booking };
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const { errors, booking } = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const payload = {
      kind,
      name: name.trim(),
      description: description.trim() || undefined,
      priceAmount: priceAmount.trim() !== '' ? Number(priceAmount) : undefined,
      unit: unit.trim() !== '' ? unit : undefined,
      discountPercent: discountPercent.trim() !== '' ? Number(discountPercent) : undefined,
      localAliases: localAliasesText.trim() !== '' ? parseAliases(localAliasesText) : undefined,
      status,
      booking: kind === 'SERVICE' && bookingEnabled ? (booking as never) : undefined,
      inWhatsAppCatalog: kind === 'PRODUCT' ? inWhatsAppCatalog : false,
      category: category.trim() || (isEdit ? null : undefined),
      catalogChannelIds: kind === 'PRODUCT' ? catalogChannelIds : [],
    };

    try {
      if (isEdit && id) {
        await update.mutateAsync({ ...payload, imageAssetId });
      } else {
        await create.mutateAsync({ ...payload, imageAssetId: imageAssetId ?? undefined });
      }
      toast.success(isEdit ? 'Saved.' : 'Created.');
      navigate('/products-services', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          const mapped: Record<string, string> = {};
          for (const [k, message] of Object.entries(serverFieldErrors)) {
            mapped[k.startsWith('booking') ? 'booking' : k] = message;
          }
          setFieldErrors(mapped);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500 ? err.message : 'Could not save.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 700 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit product/service' : 'New product/service'}
      </h1>
      <Card>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <Select
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ProductServiceKind)}
            options={KIND_OPTIONS}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />
          <ProductImageField
            value={imageAssetId}
            onChange={(next) => {
              setImageAssetId(next);
              clearFieldError('imageAssetId');
            }}
            error={fieldErrors.imageAssetId}
          />
          <TextField
            label="Category (optional)"
            placeholder="e.g. Pipes - groups products in WhatsApp product messages"
            value={category}
            list={categoryListId}
            onChange={(e) => {
              setCategory(e.target.value);
              clearFieldError('category');
            }}
            error={fieldErrors.category}
          />
          <datalist id={categoryListId}>
            {(categories.data ?? []).map((c) => (
              <option key={c.name} value={c.name} />
            ))}
          </datalist>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            label="Price (optional)"
            value={priceAmount}
            onChange={(e) => {
              setPriceAmount(e.target.value);
              clearFieldError('priceAmount');
            }}
            error={fieldErrors.priceAmount}
          />
          <Select
            label="Unit (optional)"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            options={UNIT_OPTIONS}
          />
          <TextField
            label="Discount % (optional)"
            value={discountPercent}
            onChange={(e) => {
              setDiscountPercent(e.target.value);
              clearFieldError('discountPercent');
            }}
            error={fieldErrors.discountPercent}
          />
          {priceAmount.trim() !== '' && !Number.isNaN(Number(priceAmount)) ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              {(() => {
                const price = Number(priceAmount);
                const pct =
                  discountPercent.trim() !== '' && !Number.isNaN(Number(discountPercent))
                    ? Number(discountPercent)
                    : 0;
                const discounted = Number((price * (1 - pct / 100)).toFixed(2));
                return pct > 0
                  ? `Discounted price: ${discounted} (was ${price})`
                  : `Price charged: ${discounted}`;
              })()}
            </p>
          ) : null}
          <TextField
            label="Local-language names (optional, comma-separated)"
            value={localAliasesText}
            onChange={(e) => setLocalAliasesText(e.target.value)}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ProductServiceStatus)}
            options={STATUS_OPTIONS}
          />

          {kind === 'PRODUCT' ? (
            <div className="ne-field">
              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={inWhatsAppCatalog}
                  onChange={(e) => setInWhatsAppCatalog(e.target.checked)}
                />
                Show in WhatsApp catalog
              </label>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                Customers see it (photo, name, price) in your WhatsApp catalog and product messages.
                Needs a price, and a photo or the catalog&apos;s fallback photo.
              </span>
              {inWhatsAppCatalog && (whatsappChannels.data?.length ?? 0) > 1 ? (
                <fieldset
                  style={{
                    border: 'none',
                    margin: 'var(--space-2) 0 0',
                    padding: 0,
                    display: 'grid',
                    gap: 'var(--space-1)',
                  }}
                >
                  <legend className="ne-field__label">WhatsApp numbers</legend>
                  <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={catalogChannelIds.length === 0}
                      onChange={(e) => {
                        setCatalogChannelIds(
                          e.target.checked ? [] : [whatsappChannels.data![0]!.id],
                        );
                        clearFieldError('catalogChannelIds');
                      }}
                    />
                    All numbers
                  </label>
                  {catalogChannelIds.length > 0
                    ? whatsappChannels.data!.map((c) => (
                        <label
                          key={c.id}
                          style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                        >
                          <input
                            type="checkbox"
                            checked={catalogChannelIds.includes(c.id)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...catalogChannelIds, c.id]
                                : catalogChannelIds.filter((x) => x !== c.id);
                              // Unticking the last number means every number again.
                              setCatalogChannelIds(next);
                              clearFieldError('catalogChannelIds');
                            }}
                          />
                          {channelLabel(c)}
                        </label>
                      ))
                    : null}
                  {fieldErrors.catalogChannelIds ? (
                    <span className="ne-field__error" role="alert">
                      {fieldErrors.catalogChannelIds}
                    </span>
                  ) : null}
                </fieldset>
              ) : null}
            </div>
          ) : null}

          {kind === 'SERVICE' ? (
            <div className="ne-field">
              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={bookingEnabled}
                  onChange={(e) => setBookingEnabled(e.target.checked)}
                />
                Bookable via the appointment-booking WhatsApp flow
              </label>
              {bookingEnabled ? (
                <div className="ne-field" style={{ marginTop: 'var(--space-2)' }}>
                  <label className="ne-field__label" htmlFor={bookingFieldId}>
                    Weekly booking schedule (JSON — slotMinutes, timezone, weeklyHours)
                  </label>
                  <textarea
                    id={bookingFieldId}
                    className={['ne-input', fieldErrors.booking ? 'ne-input--error' : '']
                      .filter(Boolean)
                      .join(' ')}
                    rows={14}
                    style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}
                    value={bookingText}
                    aria-invalid={fieldErrors.booking ? true : undefined}
                    onChange={(e) => {
                      setBookingText(e.target.value);
                      clearFieldError('booking');
                    }}
                  />
                  {fieldErrors.booking ? (
                    <span className="ne-field__error" role="alert">
                      {fieldErrors.booking}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/products-services')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/products-services">← All products & services</Link>
      </p>
    </div>
  );
}
